import React, { useEffect, useState } from 'react';
import { Loader2, Save, Play, RadarIcon } from 'lucide-react';
import { useStrategyLabStore } from '../../stores/strategyLabStore';
import { strategyLabApi } from '../../lib/api';
import type { Scanner, ScanGroupNode, ScanConditionNode, ScanTestRequest } from '../../types/strategyLab';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '../ui/select';
import ScannerTreeBuilder, { newGroup } from './ScannerTreeBuilder';
import ScanPreviewTable from './ScanPreviewTable';

/** Named market universes the scanner can run over (stored in `scanner.universe`). */
const NAMED_UNIVERSES: { value: string; label: string }[] = [
  { value: 'all', label: 'Full US market' },
  { value: 'sp500', label: 'S&P 500' },
  { value: 'nasdaq100', label: 'NASDAQ 100' },
  { value: 'small_cap', label: 'Small caps' },
  { value: 'most_active', label: 'Most active' },
];

/** True if every condition in the tree has a field selected (backend fail-closes on gaps). */
function treeHasCompleteCondition(node: ScanGroupNode): boolean {
  let any = false;
  const walk = (n: ScanGroupNode | ScanConditionNode) => {
    if (n.type === 'condition') {
      if (n.field) any = true;
    } else {
      n.children.forEach(walk);
    }
  };
  walk(node);
  return any;
}

interface ScannerBuilderProps {
  editing: Scanner | null;
  onDone: () => void;
}

const ScannerBuilder: React.FC<ScannerBuilderProps> = ({ editing, onDone }) => {
  const createScanner = useStrategyLabStore((s) => s.createScanner);
  const updateScanner = useStrategyLabStore((s) => s.updateScanner);
  const testScan = useStrategyLabStore((s) => s.testScan);
  const clearScanTest = useStrategyLabStore((s) => s.clearScanTest);
  const scanTestResult = useStrategyLabStore((s) => s.scanTestResult);
  const scanTestLoading = useStrategyLabStore((s) => s.scanTestLoading);
  const scanTestError = useStrategyLabStore((s) => s.scanTestError);
  const actionError = useStrategyLabStore((s) => s.actionError);
  const watchlists = useStrategyLabStore((s) => s.watchlists);
  const fetchWatchlists = useStrategyLabStore((s) => s.fetchWatchlists);

  const [name, setName] = useState('');
  const [universe, setUniverse] = useState('all');
  const [root, setRoot] = useState<ScanGroupNode>(() => newGroup('and'));
  // Test scope: 'universe' runs the named universe; otherwise a watchlist id.
  const [testScope, setTestScope] = useState<string>('universe');
  const [testLimit] = useState(50);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (watchlists.length === 0) fetchWatchlists();
  }, [watchlists.length, fetchWatchlists]);

  useEffect(() => {
    clearScanTest();
    if (editing) {
      setName(editing.name);
      setUniverse(editing.universe || 'all');
      setRoot(editing.root && editing.root.type === 'group' ? editing.root : newGroup('and'));
    } else {
      setName('');
      setUniverse('all');
      setRoot(newGroup('and'));
    }
    setTestScope('universe');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  const complete = treeHasCompleteCondition(root);

  const handleTest = () => {
    const req: ScanTestRequest = { root, limit: testLimit };
    if (testScope === 'universe') {
      req.universe = universe;
    } else {
      req.watchlist_id = testScope;
    }
    testScan(req);
  };

  const handleSave = async () => {
    setSaving(true);
    const payload = { name: name.trim(), root, universe };
    const result = editing
      ? await updateScanner(editing.id, payload)
      : await createScanner(payload);
    setSaving(false);
    if (result) onDone();
  };

  const canSave = name.trim().length > 0 && complete && !saving;

  return (
    <Card className="bg-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <RadarIcon className="w-5 h-5 text-primary" />
          {editing ? 'Edit scanner' : 'New scanner'}
        </CardTitle>
        <CardDescription>
          Combine day-trading triggers into an AND/OR tree, then test against a universe or watchlist.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">Scanner name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Gap &amp; go" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">Default universe</Label>
            <Select value={universe} onValueChange={setUniverse}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {NAMED_UNIVERSES.map((u) => (
                  <SelectItem key={u.value} value={u.value}>
                    {u.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <ScannerTreeBuilder root={root} onChange={setRoot} />

        {/* Test controls */}
        <div className="rounded-lg border border-border p-4 space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[200px]">
              <Label className="text-xs text-muted-foreground mb-1 block">Test against</Label>
              <Select value={testScope} onValueChange={setTestScope}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="universe">
                    Universe: {NAMED_UNIVERSES.find((u) => u.value === universe)?.label ?? universe}
                  </SelectItem>
                  {watchlists.map((wl) => (
                    <SelectItem key={wl.id} value={wl.id}>
                      Watchlist: {wl.name} ({wl.symbol_count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={handleTest} disabled={!complete || scanTestLoading}>
              {scanTestLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
              Test scan
            </Button>
            {!complete && (
              <span className="text-xs text-amber-500">Add at least one condition with a field to test.</span>
            )}
          </div>

          {scanTestError && <p className="text-sm text-rose-500">{scanTestError}</p>}
          {scanTestResult && <ScanPreviewTable result={scanTestResult} />}
        </div>

        {actionError && <p className="text-sm text-rose-500">{actionError}</p>}

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onDone} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!canSave}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {editing ? 'Save changes' : 'Create scanner'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ScannerBuilder;
